import { Switch } from '@affine/component';
import type { FolderRole } from '@affine/core/modules/folder-api';
import {
  PermissionRoleMenu,
  type PermissionRoleMenuOption,
} from '@affine/core/modules/permissions/components/permission-role-menu';
import {
  createObjectPermissionRoleOptions,
  createWorkspaceCappedFolderRoleOptions,
} from '@affine/core/modules/permissions/utils/object-permission-ui';
import { objectRoleLabel } from '@affine/core/modules/permissions/utils/object-role-labels';
import * as accessStyles from '@affine/core/modules/permissions/views/permission-access-list.css';
import {
  matchesPermissionQuery,
  type PermissionInviteMode,
  type PermissionInviteTarget,
  usePermissionInviteCopy,
} from '@affine/core/modules/permissions/views/permission-create-modal-core';
import { PermissionExpandableSection } from '@affine/core/modules/permissions/views/permission-section';
import { FolderRole as FolderRoleEnum, Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import {
  InformationFillDuotoneIcon,
  InvisibleIcon,
  ViewIcon,
} from '@blocksuite/icons/rc';
import { useCallback, useMemo } from 'react';

import {
  type FolderCreatePermissionVariant,
  type FolderPermissionDialogMode,
  type FolderPermissionDialogPolicy,
  type FolderPermissionVisibility,
} from '../folder-permission-policy';
import * as styles from './folder-share-modal.css';

export {
  type FolderCreatePermissionVariant,
  folderPermissionDialogHasAdvancedSettings,
  type FolderPermissionDialogMode,
  type FolderPermissionDialogPolicy,
  type FolderPermissionDialogVariant,
  type FolderPermissionVisibility,
  getDefaultFolderCreateSettings,
  getFolderPermissionDialogPolicy,
  resolveFolderPermissionVisibility,
} from '../folder-permission-policy';
export type {
  PendingPermissionGroupInvite,
  PendingPermissionInvite,
} from '@affine/core/modules/permissions/views/permission-create-modal-core';
export { usePendingPermissionAssignments } from '@affine/core/modules/permissions/views/permission-create-modal-core';

export const FOLDER_DIRECT_ASSIGNABLE_ROLES: readonly FolderRole[] = [
  FolderRoleEnum.Editor,
  FolderRoleEnum.Reader,
  FolderRoleEnum.Admin,
];

export const FOLDER_OVERRIDE_ASSIGNABLE_ROLES: readonly FolderRole[] = [
  ...FOLDER_DIRECT_ASSIGNABLE_ROLES,
  FolderRoleEnum.None,
];

export type FolderRoleSelection = FolderRole | 'inherited';

export const folderCreateAssignableRoles = (
  _variant: FolderCreatePermissionVariant
): readonly string[] => FOLDER_DIRECT_ASSIGNABLE_ROLES;

export const matchesFolderPermissionQuery = (
  query: string,
  values: readonly (string | undefined)[]
) => matchesPermissionQuery(query, values);

export function createObjectRoleOptions<Role extends string>(
  t: ReturnType<typeof useI18n>,
  roles: readonly Role[]
): PermissionRoleMenuOption<Role>[] {
  return createObjectPermissionRoleOptions(t, roles);
}

export function useObjectRoleOptions<Role extends string>(
  roles: readonly Role[]
): PermissionRoleMenuOption<Role>[] {
  const t = useI18n();
  return useMemo(() => createObjectRoleOptions(t, roles), [roles, t]);
}

export function useFolderCreateRoleOptions(
  variant: FolderCreatePermissionVariant
) {
  const roles = useMemo(() => folderCreateAssignableRoles(variant), [variant]);
  const roleOptions = useObjectRoleOptions(roles);
  const defaultRole = roleOptions[0]?.role ?? 'Reader';

  return useMemo(
    () => ({
      defaultRole,
      roleOptions,
    }),
    [defaultRole, roleOptions]
  );
}

export function useObjectRoleLabel(t: ReturnType<typeof useI18n>) {
  return useCallback((role: string) => objectRoleLabel(t, role), [t]);
}

export function useFolderPermissionInviteCopy(
  mode: PermissionInviteMode,
  target: PermissionInviteTarget
) {
  return usePermissionInviteCopy(mode, target);
}

export const FolderPermissionExpandableSection = PermissionExpandableSection;

interface FolderVisibilitySectionProps {
  visibility: FolderPermissionVisibility;
  disabled?: boolean;
  publicLabel: string;
  privateLabel: string;
  publicDescription: string;
  privateDescription: string;
  switchTestId?: string;
  onSetVisibility: (
    visibility: FolderPermissionVisibility
  ) => void | Promise<void>;
}

export const FolderVisibilitySection = ({
  visibility,
  disabled = false,
  publicLabel,
  privateLabel,
  publicDescription,
  privateDescription,
  switchTestId,
  onSetVisibility,
}: FolderVisibilitySectionProps) => {
  const isPublic = visibility === 'public';

  return (
    <div className={styles.settingsSection}>
      <div className={styles.settingRow}>
        <div className={styles.settingLabel}>
          {isPublic ? (
            <ViewIcon className={styles.settingIcon} />
          ) : (
            <InvisibleIcon className={styles.settingIcon} />
          )}
          <span>{isPublic ? publicLabel : privateLabel}</span>
        </div>
        <Switch
          checked={isPublic}
          disabled={disabled}
          data-testid={switchTestId}
          onChange={(checked: boolean) => {
            void onSetVisibility(checked ? 'public' : 'private');
          }}
        />
      </div>
      <div className={styles.settingDescription}>
        {isPublic ? publicDescription : privateDescription}
      </div>
    </div>
  );
};

interface FolderPermissionAdvancedSettingsProps {
  disabled?: boolean;
  hasParent: boolean;
  inheritanceSourceName?: string | null;
  inheritanceSourceType?: 'folder' | 'workspace';
  inheritPermissions: boolean;
  mode: FolderPermissionDialogMode;
  onSetInheritPermissions: (inherit: boolean) => void | Promise<void>;
  onSetVisibility: (
    visibility: FolderPermissionVisibility
  ) => void | Promise<void>;
  policy: FolderPermissionDialogPolicy;
  visibility: FolderPermissionVisibility;
  visibilitySwitchTestId?: string;
}

export const FolderPermissionAdvancedSettings = ({
  disabled = false,
  hasParent,
  inheritanceSourceName,
  inheritanceSourceType,
  inheritPermissions,
  mode,
  onSetInheritPermissions,
  onSetVisibility,
  policy,
  visibility,
  visibilitySwitchTestId,
}: FolderPermissionAdvancedSettingsProps) => {
  const t = useI18n();
  const createInheritanceCopy = {
    inheritedDescription:
      t[
        'com.streamlain.settings.workspace.folders.modal.inheritance-break.inherited-description'
      ]?.() ?? 'Standard: Permissions from the parent folder apply here too.',
    interruptedDescription:
      t[
        'com.streamlain.settings.workspace.folders.modal.inheritance-break.interrupted-description'
      ]?.() ?? 'This folder has its own permission boundary.',
    title:
      t[
        'com.streamlain.settings.workspace.folders.modal.inheritance-break.label'
      ]?.() ?? 'Break inheritance',
  };
  const shareInheritanceCopy = {
    inheritedDescription:
      t['com.streamlain.folder-share.inheritance-break.inherited']?.() ??
      'Standard: Permissions from the parent folder apply here too.',
    interruptedDescription:
      t['com.streamlain.folder-share.inheritance-break.interrupted']?.() ??
      'This folder has its own permission boundary.',
    title:
      t['com.streamlain.folder-share.inheritance-break.label']?.() ??
      'Break inheritance',
  };
  const inheritanceCopy =
    mode === 'create' ? createInheritanceCopy : shareInheritanceCopy;
  const inheritedSourceDescription =
    inheritanceSourceType === 'workspace'
      ? (t[
          'com.streamlain.folder-share.inheritance-break.inherited-workspace'
        ]?.() ?? 'Default: permissions from the Public Area apply here too.')
      : inheritanceSourceType === 'folder' && inheritanceSourceName
        ? (t[
            'com.streamlain.folder-share.inheritance-break.inherited-folder'
          ]?.({ name: inheritanceSourceName }) ??
          `Default: permissions from "${inheritanceSourceName}" apply here too.`)
        : inheritanceCopy.inheritedDescription;
  const noParentDescription =
    mode === 'create'
      ? (t[
          'com.streamlain.settings.workspace.folders.modal.inherit.root-disabled'
        ]?.() ?? 'Root folders have no parent to inherit from.')
      : (t['com.streamlain.folder-share.inherit.no-parent']?.() ??
        'Root folders have no parent to inherit from.');

  return (
    <>
      {policy.showInheritanceSection ? (
        <FolderInheritanceSection
          inheritPermissions={inheritPermissions}
          hasParent={hasParent}
          disabled={disabled}
          title={inheritanceCopy.title}
          noParentDescription={noParentDescription}
          inheritedDescription={inheritedSourceDescription}
          interruptedDescription={inheritanceCopy.interruptedDescription}
          onSetInheritPermissions={onSetInheritPermissions}
        />
      ) : null}

      {policy.showVisibilitySection ? (
        <FolderVisibilitySection
          visibility={visibility}
          disabled={disabled}
          switchTestId={visibilitySwitchTestId}
          publicLabel={
            mode === 'create'
              ? (t[
                  'com.streamlain.settings.workspace.folders.modal.visibility.public'
                ]?.() ?? 'Visible')
              : (t['com.streamlain.folder-share.visibility.public']?.() ??
                'Visible')
          }
          privateLabel={
            mode === 'create'
              ? (t[
                  'com.streamlain.settings.workspace.folders.modal.visibility.private'
                ]?.() ?? 'Invisible')
              : (t['com.streamlain.folder-share.visibility.private']?.() ??
                'Invisible')
          }
          publicDescription={
            mode === 'create'
              ? (t[
                  'com.streamlain.settings.workspace.folders.modal.visibility.public-description'
                ]?.() ??
                'All workspace members can see this folder row. Contents require Read access.')
              : (t[
                  'com.streamlain.folder-share.visibility.public-description'
                ]?.() ??
                'All workspace members can see this folder row. Contents require Read access.')
          }
          privateDescription={
            mode === 'create'
              ? (t[
                  'com.streamlain.settings.workspace.folders.modal.visibility.private-description'
                ]?.() ??
                'Hidden from workspace members without explicit or inherited access.')
              : (t[
                  'com.streamlain.folder-share.visibility.private-description'
                ]?.() ??
                'Hidden from workspace members without explicit or inherited access.')
          }
          onSetVisibility={onSetVisibility}
        />
      ) : null}
    </>
  );
};

interface FolderInheritanceSectionProps {
  inheritPermissions: boolean;
  hasParent: boolean;
  disabled?: boolean;
  title: string;
  noParentDescription: string;
  inheritedDescription: string;
  interruptedDescription: string;
  onSetInheritPermissions: (inherit: boolean) => void | Promise<void>;
}

export const FolderInheritanceSection = ({
  inheritPermissions,
  hasParent,
  disabled = false,
  title,
  noParentDescription,
  inheritedDescription,
  interruptedDescription,
  onSetInheritPermissions,
}: FolderInheritanceSectionProps) => {
  return (
    <div className={styles.settingsSection}>
      <div className={styles.settingRow}>
        <div className={styles.settingLabel}>
          <InformationFillDuotoneIcon className={styles.settingIcon} />
          <span>{title}</span>
        </div>
        <Switch
          checked={hasParent && !inheritPermissions}
          disabled={disabled || !hasParent}
          data-testid="folder-inheritance-switch"
          onChange={(checked: boolean) => {
            void onSetInheritPermissions(!checked);
          }}
        />
      </div>
      <div className={styles.settingDescription}>
        {!hasParent
          ? noParentDescription
          : inheritPermissions
            ? inheritedDescription
            : interruptedDescription}
      </div>
    </div>
  );
};

interface FolderPermissionRoleSelectorProps {
  currentRole: FolderRole;
  canManage: boolean;
  onChangeRole: (role: FolderRole) => void;
  getRoleLabel: (role: string) => string;
  sourceType?: 'direct' | 'folder' | 'workspace';
  sourceName?: string | null;
  isDirectOverride?: boolean;
  onResetDirectOverride?: () => void;
  workspaceRole?: Permission;
  isHouseGuest?: boolean;
}

export const FolderPermissionRoleSelector = ({
  currentRole,
  canManage,
  onChangeRole,
  getRoleLabel,
  sourceType,
  sourceName,
  isDirectOverride,
  onResetDirectOverride,
  workspaceRole,
  isHouseGuest = false,
}: FolderPermissionRoleSelectorProps) => {
  const t = useI18n();
  const isOwner = currentRole === FolderRoleEnum.Owner;
  const isInherited = sourceType === 'folder' || sourceType === 'workspace';
  const inheritedLabel =
    sourceType === 'workspace'
      ? (t['com.streamlain.folder-share.role.inherited-workspace']?.() ??
        'Inherited from Public Area')
      : sourceType === 'folder' && sourceName
        ? (t['com.streamlain.folder-share.role.inherited-folder']?.({
            name: sourceName,
          }) ?? `Inherited from "${sourceName}"`)
        : (t['com.streamlain.folder-share.role.inherited']?.() ??
          'Inherited from folder');
  const label = getRoleLabel(currentRole);

  if (!canManage || isOwner || isInherited) {
    return (
      <span className={accessStyles.roleLabel}>
        <span>{label}</span>
        {isInherited ? (
          <span
            className={styles.inheritedSourceLabel}
            data-testid="folder-permission-inheritance-source"
          >
            {inheritedLabel}
          </span>
        ) : null}
      </span>
    );
  }

  const selectedRole: FolderRoleSelection = currentRole;
  // Existing House guest rows stay capped at Reader/Editor. House guests can
  // no longer be created; external guests never receive folder grants.
  const assignableRoles = isHouseGuest
    ? [FolderRoleEnum.Reader, FolderRoleEnum.Editor]
    : FOLDER_OVERRIDE_ASSIGNABLE_ROLES;
  const options: FolderRoleSelection[] = isDirectOverride
    ? [...assignableRoles, 'inherited']
    : [...assignableRoles];

  return (
    <PermissionRoleMenu<FolderRoleSelection>
      role={selectedRole}
      label={label}
      options={options.map(role => {
        if (role === 'inherited') {
          return { role, label: inheritedLabel };
        }
        const capped =
          workspaceRole && !isHouseGuest
            ? createWorkspaceCappedFolderRoleOptions(
                t,
                [role],
                workspaceRole
              )[0]
            : undefined;
        return {
          role,
          label: getRoleLabel(role),
          disabled: capped?.disabled,
        };
      })}
      onSelect={role => {
        if (role === 'inherited') {
          if (isDirectOverride) {
            onResetDirectOverride?.();
          }
          return;
        }
        onChangeRole(role);
      }}
    />
  );
};
